import React, { FC, useState } from 'react';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { useDisputesContext } from '../../store/disputes/disputes-context';
import { useParams } from 'react-router-dom';
import { matchDispute } from '../../store/disputes/actions/disputes';
import { useFormik } from 'formik';
import {
	getAlertMatchDisputeModalSchema,
	getMatchDisputeModalSchema, initialMatchDisputeModalValues, MATCH_DISPUTE_FORM_FIELD, MATCH_DISPUTE_FORM_FIELD_NAMES
} from '../../constants/dispute/dispute-form';
import { FormInput } from '../merchants/FormInput';
import { InputType } from '../form/form-input-constants';
import clsx from 'clsx';
import { numberFormatter } from '../../utils/utilities';
import { ITransactionsData } from '../../store/payments/payments-interfaces-helper';

interface Props {
	checkedItem: ITransactionsData;
	closeModal: () => void;
	matchSuccess: () => void;
	matchError: (error: any) => void;
}

export const MatchDisputeModal: FC<Props> = (props) => {
	const { checkedItem, closeModal, matchSuccess, matchError } = props;
	const { disputeQueueItemId } = useParams<{ disputeQueueItemId: string }>();
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	const { disputesContextState: { isDisputeMatchLoading, kountStatusCodes, disputeDetails }, dispatch } = useDisputesContext();


	const isKount = (disputeDetails?.disputeManagementSystem === 'Kount' || disputeDetails?.disputeManagementSystem === 'KountDCM') && (
		disputeDetails?.stage !== 'CHARGEBACK' &&
		disputeDetails?.kountEventType !== 'FRAUD_NOTICE' &&
		disputeDetails?.kountEventType !== 'FRAUD_NOTICE' &&
		disputeDetails?.eventType !== 'DISPUTE_NOTICE' &&
		disputeDetails?.eventType !== 'DISPUTE_NOTICE'
	);

	const isMyRCVR = disputeDetails?.disputeManagementSystem === 'MyRCVR' &&
		(disputeDetails?.stage !== 'CHARGEBACK' &&
			disputeDetails?.eventType !== 'FRAUD_NOTICE' &&
			disputeDetails?.eventType !== 'DISPUTE_NOTICE');

	const isChargeblast = disputeDetails?.disputeManagementSystem === 'Chargeblast' && disputeDetails?.providerName === 'Ethoca';

	const isAlertProvider = isKount || isMyRCVR || isChargeblast;

		let validationSchema = getMatchDisputeModalSchema(disputeDetails);

	// if (isKount) {
	// 	validationSchema = getKountMatchDisputeModalSchema(disputeDetails);
	// }
	//
	// if (isMyRCVR) {
	// 	validationSchema = getMyRcvrMatchDisputeModalSchema(disputeDetails);
	// }

	if (isAlertProvider) {
		validationSchema = getAlertMatchDisputeModalSchema(disputeDetails);
	}

	const formik = useFormik({
		initialValues: initialMatchDisputeModalValues,
		validationSchema,
		onSubmit: () => onMatchConfirm()
	});

	const onMatchConfirm = async (): Promise<void> => {
    if (isAlertProvider && !formik.isValid) return;

		const body = {
			disputeQueueItemId,
			transactionId: checkedItem?.id,
			issueRefund: isChecked,
		};

		await matchDispute(dispatch, formik, body, matchSuccess, matchError, setError);
	};

	const getValue = (fieldName: MATCH_DISPUTE_FORM_FIELD): string => {
		const formikValue = formik?.getFieldProps(fieldName);
		return formikValue?.value;
	};


	const getPickerData = (): { value: string, label: string }[] => {
		if (disputeDetails?.kountEventType === 'ETHOCA_FRAUD' || disputeDetails?.eventType === 'ETHOCA_FRAUD') {
			return (kountStatusCodes?.ethocaFraud || [])?.map(el => ({
				value: el,
				label: el
			}));
		}

		if (disputeDetails?.kountEventType === 'ETHOCA_DISPUTE' || disputeDetails?.eventType === 'ETHOCA_DISPUTE') {
			return (kountStatusCodes?.ethocaDispute || [])?.map(el => ({
				value: el,
				label: el
			}));
		}

		if (disputeDetails?.disputeManagementSystem === 'Chargeblast' && disputeDetails?.providerName === 'Ethoca') {
			return (kountStatusCodes?.chargeblastStatusCodes || [])?.map(el => ({
				value: el,
				label: el
			}));
		}

		if (disputeDetails?.kountEventType === 'RDR' || disputeDetails?.eventType === 'RDR' ||
			disputeDetails?.kountEventType === 'DISPUTE' || disputeDetails?.eventType === 'DISPUTE' ||
			disputeDetails?.kountEventType === 'CANCEL' || disputeDetails?.eventType === 'CANCEL') {
			return Object.keys(kountStatusCodes?.verifyStatusCodes || {})?.map(el => ({
				value: el,
				label: `${el} - ${kountStatusCodes?.verifyStatusCodes?.[el]}`
			}));
		}


		return [];
	};

	return (
		<form onSubmit={formik.handleSubmit} className="px-8 pb-8">
			<div className="fs-3 mb-5">Attention! This action can`t be undone.</div>

			{
				isKount && (
					<div className="row">
						<FormInput
							formik={formik}
							key={MATCH_DISPUTE_FORM_FIELD.STATUS_CODE}
							fieldName={MATCH_DISPUTE_FORM_FIELD.STATUS_CODE}
							label={MATCH_DISPUTE_FORM_FIELD_NAMES.STATUS_CODE}
							placeholder={MATCH_DISPUTE_FORM_FIELD_NAMES.STATUS_CODE}
							required
							inputType={InputType.Select}
							data={getPickerData()}
							searchable={false}
							additionalClasses="col-sm-6 col-md-6"
						/>
						<FormInput
							formik={formik}
							key={MATCH_DISPUTE_FORM_FIELD.COMMENTS}
							fieldName={MATCH_DISPUTE_FORM_FIELD.COMMENTS}
							label={MATCH_DISPUTE_FORM_FIELD_NAMES.COMMENTS}
							placeholder={MATCH_DISPUTE_FORM_FIELD_NAMES.COMMENTS}
							inputType={InputType.Description}
							additionalClasses="col-md-12"
						/>
					</div>
				)
			}

			{
				isMyRCVR && (
					<div className="row">
						<FormInput
							formik={formik}
							key={MATCH_DISPUTE_FORM_FIELD.STATUS_CODE}
							fieldName={MATCH_DISPUTE_FORM_FIELD.STATUS_CODE}
							label={MATCH_DISPUTE_FORM_FIELD_NAMES.MY_RCVR_STATUS_CODE}
							placeholder={MATCH_DISPUTE_FORM_FIELD_NAMES.MY_RCVR_STATUS_CODE}
							required
							inputType={InputType.Select}
							data={getPickerData()}
							searchable={false}
							additionalClasses="col-sm-6 col-md-6"
						/>
						<FormInput
							formik={formik}
							key={MATCH_DISPUTE_FORM_FIELD.COMMENTS}
							fieldName={MATCH_DISPUTE_FORM_FIELD.COMMENTS}
							label={MATCH_DISPUTE_FORM_FIELD_NAMES.COMMENTS}
							placeholder={MATCH_DISPUTE_FORM_FIELD_NAMES.COMMENTS}
							inputType={InputType.Description}
							additionalClasses="col-md-12"
						/>
					</div>
				)
			}

			{
				isChargeblast && (
					<div className="row">
						<FormInput
							formik={formik}
							key={MATCH_DISPUTE_FORM_FIELD.STATUS_CODE}
							fieldName={MATCH_DISPUTE_FORM_FIELD.STATUS_CODE}
							label={MATCH_DISPUTE_FORM_FIELD_NAMES.STATUS_CODE}
							placeholder={MATCH_DISPUTE_FORM_FIELD_NAMES.STATUS_CODE}
							required
							inputType="select"
							data={getPickerData()}
							searchable={false}
							additionalClasses="col-sm-6 col-md-6"
						/>
						<FormInput
							formik={formik}
							key={MATCH_DISPUTE_FORM_FIELD.COMMENTS}
							fieldName={MATCH_DISPUTE_FORM_FIELD.COMMENTS}
							label={MATCH_DISPUTE_FORM_FIELD_NAMES.COMMENTS}
							placeholder={MATCH_DISPUTE_FORM_FIELD_NAMES.COMMENTS}
							inputType="description"
							additionalClasses="col-md-12"
						/>
					</div>
				)
			}

			<div className="row">
				<FormInput
					formik={formik}
					key={MATCH_DISPUTE_FORM_FIELD.AMOUNT}
					fieldName={MATCH_DISPUTE_FORM_FIELD.AMOUNT}
					label={MATCH_DISPUTE_FORM_FIELD_NAMES.AMOUNT}
					placeholder={`Enter amount up to $${numberFormatter(disputeDetails?.amount)}`}
					inputType={InputType.Number}
					additionalClasses="col-sm-6 col-md-6"
					disabled={!isChecked}
					isLabelIcon={true}
					tooltipText={`To issue a partial refund, first, check the 'Issue Refund' option. Then, enter the desired refund amount, ensuring it does not exceed the maximum allowable refund amount of $${disputeDetails?.amount}`}
				/>
				<div className="d-flex align-items-center ps-8 col-6 pb-2">
					<div className="form-check form-check-custom form-check-solid">
						<input
							className="form-check-input"
							type="checkbox"
							checked={isChecked}
							onChange={(): void => setIsChecked(!isChecked)}
						/>
					</div>
					<div className="fs-6 ms-2">Issue Refund</div>
				</div>
			</div>


			<div className={clsx('text-danger fs-6 mt-2', !error && 'hidden h-20px')}>{error}</div>


			<div className="text-center mt-5">
				<ButtonComponent
					buttonName="Cancel"
					buttonType={BUTTON_TYPE.CANCELLED}
					additionalClass="btn-sm"
					onButtonClick={closeModal}
				/>
				<ButtonComponent
					buttonName={isChecked ? 'Refund & Match' : 'Match'}
					additionalClass={clsx('btn-sm ms-10', isChecked ? 'w-200px' : 'w-100px')}
					type="submit"
					// onButtonClick={onMatchConfirm}
					loading={isDisputeMatchLoading}
					isDisabled={isDisputeMatchLoading}
				/>
			</div>
		</form>
	);
};
